import React from "react";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";
import { MenuItem, SelectChangeEvent } from "@mui/material";
import { ENGLISH_ABBREV, SPANISH_ABBREV } from "@bto/shared";
import { useBooleanFeatureFlag } from "../../utils/hooks/useFeatureFlag";
import { FeatureFlags } from "../../utils/launchDarklyUtils";
import {
  normalizeLanguage,
  persistLanguagePreference,
} from "../../i18n/languagePreference";
import { getCookieAccessToken } from "../../utils/requestUtils";
import { UpdateLanguagePreference } from "../Profile/Services/UpdateProfileInfoService";
import { patientState } from "../../recoil/atoms";
import {
  LanguageSwitcherLabel,
  LanguageSwitcherWrapper,
  StyledFormControl,
  StyledSelect,
} from "./LanguageSwitcher.styles";

interface LanguageSwitcherProps {
  showLanguageSwitcher?: boolean;
  requireFeatureFlag?: boolean;
  variant?: "compact" | "settings";
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  showLanguageSwitcher = false,
  requireFeatureFlag = true,
  variant = "compact",
}) => {
  const { i18n, t } = useTranslation();
  const { enabled: isLanguageToggleEnabled } = useBooleanFeatureFlag(
    FeatureFlags.PatientPortalSpanishLanguageToggle,
  );
  const setPatientInfo = useSetRecoilState(patientState);

  const handleLanguageChange = (event: SelectChangeEvent<unknown>) => {
    const selectedLanguage = normalizeLanguage(event.target.value as string);
    i18n.changeLanguage(selectedLanguage);
    if (isLanguageToggleEnabled) {
      persistLanguagePreference(selectedLanguage);
    }
    // Keep Recoil client state in sync with the displayed locale so readers of
    // patientState.user.language (e.g. the apply-saved-preference effect in
    // AppContainer) don't see a stale value until the next PHI refresh. No-ops
    // when unauthenticated (no patient in state).
    setPatientInfo((prev) =>
      prev
        ? { ...prev, user: { ...prev.user, language: selectedLanguage } }
        : prev,
    );
    // Persist to the user record (Customer.io language attribute) only when the
    // patient is authenticated. Best-effort: the local UI change stands regardless.
    const accessToken = getCookieAccessToken();
    if (accessToken) {
      UpdateLanguagePreference(selectedLanguage, accessToken).catch(
        () => undefined,
      );
    }
  };

  if (
    !showLanguageSwitcher ||
    (requireFeatureFlag && !isLanguageToggleEnabled)
  ) {
    return null;
  }

  const normalizedLanguage = normalizeLanguage(i18n.language);

  return (
    <LanguageSwitcherWrapper data-testid="language-switcher" $variant={variant}>
      <LanguageSwitcherLabel>
        {t("languageSwitcher.label")}
      </LanguageSwitcherLabel>
      <StyledFormControl size="small" $variant={variant}>
        <StyledSelect
          value={normalizedLanguage}
          onChange={handleLanguageChange}
          displayEmpty
          variant="outlined"
          inputProps={{
            "aria-label": t("languageSwitcher.label"),
            "data-testid": "language-switcher-select",
          }}
          $variant={variant}
        >
          <MenuItem value={ENGLISH_ABBREV}>
            {t("languageSwitcher.options.en")}
          </MenuItem>
          <MenuItem value={SPANISH_ABBREV}>
            {t("languageSwitcher.options.es")}
          </MenuItem>
        </StyledSelect>
      </StyledFormControl>
    </LanguageSwitcherWrapper>
  );
};
